import * as Api from '@ViewModels';
import moment from 'moment';
import { useState } from 'react';
import { useAdminUserSession } from '../../../../models/hooks/adminStateHooks';
import {
	invalidateAccountAdminQuery,
	setAccountAdminQueryData,
} from '../../../../queries/Admin/Account/useAdminAccountQuery';
import { useUpdateNextMeetingMutation } from '../../../../queries/Admin/Account/useUpdateNextMeetingMutation';
import { AdminPermission } from '../../../../viewmodels/AdminViewModels';
import { Button } from '../../../../web/components-v2/Button';
import { Calendar } from '../../../../web/components-v2/Calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../web/components-v2/Popover';
import { nextMeetingDescription } from '../accountOverviewFormatters';
import { accountNextMeetingFieldStyles as s } from './accountNextMeetingFieldStyles';

/**
 * Read-mode Next Meeting value: clicking the date opens a calendar and the picked day is saved
 * straight away through `PUT admin/account/{id}/additionalInfo/nextMeeting` — in place, independent
 * of the card's Edit mode (legacy parity, and the account PUT doesn't persist this field anyway).
 * Without `ModifyAccount` it renders as plain text.
 */
export const AccountNextMeetingField = ({ account }: { account: Api.IAccount }) => {
	const canEdit = useAdminUserSession().hasPermission(AdminPermission.ModifyAccount);
	const mutation = useUpdateNextMeetingMutation();
	const [isOpen, setIsOpen] = useState(false);

	const display = nextMeetingDescription(account);

	if (!canEdit) {
		return <>{display}</>;
	}

	// Stored as a UTC-midnight date, so re-read it as a local calendar day: parsing it locally would
	// select (and then re-send) the day before west of UTC.
	const today = moment().startOf('day');
	const storedDay = account.additionalInfo?.nextMeeting
		? moment(moment.utc(account.additionalInfo.nextMeeting).format('YYYY-MM-DD'))
		: undefined;
	// Past days are disabled, so opening on the month of an elapsed meeting — the usual state when an
	// admin comes to book the next one — would show a month with nothing selectable.
	const openMonth = storedDay?.isSameOrAfter(today, 'day') ? storedDay : today;

	const onSelectDay = (day?: Date) => {
		// Re-clicking the selected day clears it, so the picker hands back nothing; dismiss either way
		// rather than leaving the popover open on a click that did something.
		setIsOpen(false);
		if (!day) {
			return;
		}
		mutation.mutate(
			{ accountId: account.id, nextMeeting: day },
			{
				onSuccess: saved => {
					// Publish the PUT result before invalidating: a card mid-edit seeds its draft from this
					// account, and a refetch-shaped gap there would let its save re-send the pre-pick date.
					if (saved) {
						setAccountAdminQueryData(saved);
					}
					invalidateAccountAdminQuery();
				},
			}
		);
	};

	return (
		<Popover open={isOpen} onOpenChange={setIsOpen}>
			<PopoverTrigger asChild>
				<Button
					variant='link'
					styles={[s.trigger]}
					disabled={mutation.isPending}
					aria-label={`Next meeting: ${display}. Click to change`}
				>
					{display}
				</Button>
			</PopoverTrigger>
			<PopoverContent align='start' side='bottom'>
				<Calendar
					mode='single'
					selected={storedDay?.toDate()}
					defaultMonth={openMonth.toDate()}
					disabled={{ before: today.toDate() }}
					onSelect={onSelectDay}
				/>
			</PopoverContent>
		</Popover>
	);
};
